import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Phone, MapPin, BadgePercent, Check, Percent, Euro, Wrench } from "lucide-react";
import { IMAGES, METIERS, MANIFESTO, PROCESS, BRANDS_CLIM, BRANDS_FROID } from "../data/content";
import { Reveal, LineReveal, useSeo } from "../lib/motion";
import { CTASection } from "../components/CTASection";
import { useSettings, isSet, telHref } from "../context/SettingsContext";
import { api, fileUrl } from "../lib/api";

function Hero() {
  const ref = useRef(null);
  const { settings } = useSettings();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-[86vh] lg:min-h-[80vh] flex items-end overflow-hidden bg-ink">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={settings.hero_url ? settings.hero_url : IMAGES.hero} alt="Installation de climatisation en Alsace" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/20" />
        <div className="absolute inset-0 tech-grid opacity-30" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pb-16 pt-28">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-icelight mb-6">
            <MapPin size={14} /> Alsace · Bas-Rhin
          </span>
        </motion.div>

        <h1 className="font-heading font-black text-white tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl">
          <LineReveal delay={0.15} lines={["Climatisation & froid", "professionnel en Alsace"]} />
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
          className="text-white/70 text-lg md:text-xl mt-6 max-w-xl">
          Installation, entretien et dépannage pour particuliers et professionnels.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
          className="flex flex-wrap gap-4 mt-8">
          <Link to="/demande-de-devis" data-testid="hero-devis-btn" className="inline-flex items-center gap-2 bg-ice text-white rounded-md px-8 py-4 font-bold hover:bg-icehover transition-transform hover:scale-105">
            Demander un devis <ArrowUpRight size={18} />
          </Link>
          {isSet(settings.phone) ? (
            <a href={telHref(settings.phone)} data-testid="hero-phone-btn" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/25 text-white rounded-md px-8 py-4 font-bold hover:bg-white/20 transition-colors">
              <Phone size={18} /> Nous appeler
            </a>
          ) : (
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/25 text-white rounded-md px-8 py-4 font-bold hover:bg-white/20 transition-colors">
              <Phone size={18} /> Nous contacter
            </Link>
          )}
          <Link to="/depannage" data-testid="hero-depannage-btn" className="inline-flex items-center gap-2 bg-red-500 text-white rounded-md px-8 py-4 font-bold hover:bg-red-600 transition-colors">
            <Wrench size={18} /> Dépannage
          </Link>
        </motion.div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.7 }}
          className="text-white/50 font-mono text-sm mt-8 tracking-wide">
          Climatisation · Gainable · Froid commercial
        </motion.p>
      </div>
    </section>
  );
}

function Metiers() {
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <Reveal className="mb-10 max-w-2xl">
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-ice">Nos métiers</span>
        <h2 className="font-heading font-black tracking-tight text-3xl md:text-5xl text-ink mt-4 leading-[1.03]">Trois expertises, une même exigence technique.</h2>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6">
        {METIERS.map((m, i) => (
          <Reveal key={m.key} delay={i * 0.1}>
            <Link to={m.to} data-testid={`metier-card-${m.key}`} className="group flex flex-col h-full bg-white border border-line rounded-md overflow-hidden hover:shadow-xl hover:border-ice/40 transition-[box-shadow,border-color] duration-300">
              <div className="clip-frame aspect-[16/10] relative">
                <img src={m.image} alt={m.title} className="w-full h-full object-cover object-center" />
                <span className="absolute top-3 left-3 font-mono text-[11px] font-bold text-white/90">{m.num}</span>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-heading font-bold text-xl text-ink">{m.title}</h3>
                <p className="text-maintext mt-2.5 leading-relaxed text-[15px]">{m.text}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {m.links.map((l) => <span key={l} className="text-xs font-semibold text-muted2 bg-cloud border border-line rounded px-2.5 py-1">{l}</span>)}
                </div>
                <span className="inline-flex items-center gap-1.5 text-ice font-bold text-sm mt-5 group-hover:gap-3 transition-[gap]">{m.cta} <ArrowRight size={16} /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="bg-ink text-white relative overflow-hidden">
      <div className="absolute inset-0 tech-grid opacity-20" aria-hidden />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-11 md:py-14 relative">
        <Reveal className="mb-10 max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-icelight">Notre approche</span>
          <h2 className="font-heading font-black tracking-tight text-3xl md:text-5xl mt-4 leading-[1.03]">Une approche technique avant tout.</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.num} delay={i * 0.08}>
              <div className="flex gap-5 border-t border-white/10 pt-5">
                <span className="font-heading font-black text-4xl md:text-5xl text-white/15 leading-none">{m.num}</span>
                <div>
                  <h3 className="font-heading font-bold text-lg md:text-xl">{m.title}</h3>
                  <p className="text-white/55 mt-1.5 leading-relaxed text-[15px]">{m.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function SplitBlock({ label, title, children, image, to, cta, reverse }) {
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-14">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal className={reverse ? "lg:order-2" : ""}>
          <div className="clip-frame rounded-md aspect-[5/4] border border-line">
            <img src={image} alt={title} className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className={reverse ? "lg:order-1" : ""}>
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-ice">{label}</span>
          <h2 className="font-heading font-black tracking-tight text-3xl md:text-4xl text-ink mt-4 leading-[1.05]">{title}</h2>
          <div className="text-maintext text-lg mt-5 space-y-4 leading-relaxed">{children}</div>
          <Link to={to} className="inline-flex items-center gap-2 bg-ink text-white rounded-md px-7 py-3.5 font-bold mt-7 hover:bg-inkhover transition-transform hover:scale-105">
            {cta} <ArrowUpRight size={17} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function RealisationsPreview() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/realisations").then(({ data }) => setItems(data.slice(0, 3))).catch(() => {}); }, []);
  if (items.length === 0) return null;
  return (
    <section className="bg-cloud">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div className="max-w-xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-ice">Réalisations</span>
            <h2 className="font-heading font-black tracking-tight text-3xl md:text-5xl text-ink mt-4 leading-[1.03]">Nos dernières réalisations.</h2>
          </div>
          <Link to="/realisations" className="inline-flex items-center gap-2 font-bold text-ink hover:text-ice transition-colors">Voir toutes nos réalisations <ArrowRight size={17} /></Link>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.07}>
              <Link to="/realisations" className="group block bg-white border border-line rounded-md overflow-hidden">
                <div className="clip-frame aspect-[4/3]">
                  <img src={fileUrl(r.images?.[0]) || IMAGES.clim} alt={r.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-bold text-ink">{r.title}</h3>
                  <p className="text-sm text-muted2 mt-1">{r.city}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-14">
      <Reveal className="mb-10 max-w-2xl">
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-ice">Comment ça se passe</span>
        <h2 className="font-heading font-black tracking-tight text-3xl md:text-5xl text-ink mt-4 leading-[1.03]">Un projet, quatre étapes claires.</h2>
      </Reveal>
      <div className="relative">
        <div className="hidden lg:block absolute top-[26px] left-[12.5%] right-[12.5%] h-px bg-line" aria-hidden />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {PROCESS.map((p, i) => (
            <Reveal key={p.num} delay={i * 0.08}>
              <div className="relative">
                <span className="relative z-10 inline-flex items-center justify-center w-13 h-13 rounded-full bg-ink text-white font-mono text-sm font-bold" style={{ width: 52, height: 52 }}>{p.num}</span>
                <h3 className="font-heading font-bold text-lg text-ink mt-5">{p.title}</h3>
                <p className="text-maintext mt-2 leading-relaxed text-[15px]">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ZoneAlsace() {
  return (
    <section className="bg-cloud">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-14 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-ice">Zone d’intervention</span>
          <h2 className="font-heading font-black tracking-tight text-3xl md:text-5xl text-ink mt-4 leading-[1.03]">HP Froid intervient en Alsace.</h2>
          <p className="text-maintext text-lg mt-5 leading-relaxed">Entreprise basée dans le secteur de Strasbourg / Illkirch-Graffenstaden.</p>
          <p className="text-maintext text-lg mt-3 leading-relaxed">Nous intervenons principalement dans le Bas-Rhin et, selon les projets, dans les secteurs environnants.</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative rounded-md aspect-[4/3] bg-ink overflow-hidden border border-ink">
            <div className="absolute inset-0 tech-grid opacity-40" aria-hidden />
            <svg viewBox="0 0 200 220" className="absolute inset-0 w-full h-full p-8" role="img" aria-label="Carte simplifiée du Bas-Rhin, Alsace">
              <path d="M96 12 L120 22 L128 46 L150 58 L146 86 L160 104 L150 132 L158 160 L140 184 L120 200 L98 208 L86 188 L70 178 L74 150 L60 128 L70 104 L58 82 L70 58 L82 40 Z"
                fill="rgba(56,189,248,0.08)" stroke="#38BDF8" strokeWidth="1.5" strokeLinejoin="round" />
              <circle cx="112" cy="70" r="5" fill="#38BDF8" />
              <circle cx="112" cy="70" r="12" fill="none" stroke="#38BDF8" strokeWidth="1" opacity="0.5" />
              <text x="122" y="66" fill="#fff" fontSize="9" fontFamily="monospace">Strasbourg</text>
              <text x="122" y="78" fill="rgba(255,255,255,0.5)" fontSize="7" fontFamily="monospace">Illkirch-Graffenstaden</text>
            </svg>
            <span className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-4 py-2 rounded-md font-mono text-sm font-bold text-ink flex items-center gap-2"><MapPin size={15} className="text-ice" /> Bas-Rhin · Alsace</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Marques() {
  const { settings } = useSettings();
  if (settings.brands_enabled === false) return null;
  const Wall = ({ items, testid }) => (
    <div data-testid={testid} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {items.map((b) => (
        <span key={b} className="flex items-center justify-center bg-white border border-line rounded-md px-4 py-5 text-center font-heading font-semibold text-sm md:text-base tracking-wide text-muted2/60 hover:text-ink hover:border-ice/40 transition-[color,border-color] duration-300 select-none">{b}</span>
      ))}
    </div>
  );
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-14">
      <Reveal className="mb-8 max-w-3xl">
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-ice">Équipements</span>
        <h2 className="font-heading font-black tracking-tight text-2xl md:text-3xl text-ink mt-4 leading-[1.05]">Marques et équipements que nous installons et utilisons.</h2>
      </Reveal>
      <div className="space-y-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted2 mb-4">Climatisation / PAC</p>
          <Wall items={BRANDS_CLIM} testid="brands-clim" />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted2 mb-4">Froid commercial / réfrigération / régulation</p>
          <Wall items={BRANDS_FROID} testid="brands-froid" />
        </div>
      </div>
    </section>
  );
}

const AIDE_CARDS = [
  { key: "cee", icon: Euro, title: "Aide CEE", sub: "Prime énergie possible selon le projet", to: "/demande-de-devis?aide=cee" },
  { key: "tva10", icon: Percent, title: "Travaux TVA 10 %", sub: "Pour certains travaux en rénovation", to: "/demande-de-devis?aide=tva10" },
  { key: "tva55", icon: BadgePercent, title: "Travaux TVA 5,5 %", sub: "Selon conditions d’éligibilité", to: "/demande-de-devis?aide=tva55" },
];

function AidesBadges() {
  return (
    <section className="border-b border-line bg-cloud">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 md:py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-ice"><BadgePercent size={15} /> Vos aides &amp; taux réduits</span>
            <p className="text-maintext mt-2 text-base md:text-lg">Vérifiez rapidement les dispositifs applicables à votre projet.</p>
          </div>
          <Link to="/aides" data-testid="verifier-eligibilite" className="inline-flex items-center gap-1.5 text-ice font-bold text-sm hover:gap-3 transition-[gap]">
            Tout savoir sur les aides <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid gap-4 md:grid-cols-3" data-testid="aides-badges">
          {AIDE_CARDS.map((c) => (
            <Link key={c.key} to={c.to} data-testid={`aide-card-${c.key}`}
              className="group flex items-center gap-4 bg-white border border-line rounded-lg p-5 md:p-6 shadow-sm hover:shadow-xl hover:border-ice/50 hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300">
              <span className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-lg bg-ice/10 text-ice group-hover:bg-ice group-hover:text-white transition-colors">
                <c.icon size={22} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold text-lg text-ink leading-tight">{c.title}</p>
                <p className="text-sm text-muted2 mt-1 leading-snug">{c.sub}</p>
              </div>
              <ArrowUpRight size={20} className="text-line group-hover:text-ice transition-colors shrink-0" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function DepannageBand() {
  return (
    <section data-testid="home-depannage-band" className="border-t border-line bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-12 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
        <div className="flex-1">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-red-500"><Wrench size={15} /> Intervention</span>
          <h2 className="font-heading font-black tracking-tight text-2xl md:text-3xl text-ink mt-3 leading-[1.05]">Besoin d’un dépannage ?</h2>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <Link to="/depannage" data-testid="band-depannage-btn" className="inline-flex items-center gap-2 bg-red-500 text-white rounded-md px-7 py-3.5 font-bold hover:bg-red-600 transition-colors">
            Demander une intervention <ArrowUpRight size={17} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useSeo("Climatisation & Froid professionnel en Alsace", "HP Froid & Climatisation : installation, entretien et dépannage de climatisation, PAC air/air et froid commercial en Alsace (Bas-Rhin). Demandez votre devis.");
  return (
    <>
      <Hero />
      <AidesBadges />
      <Metiers />
      <SplitBlock
        label="Climatisation"
        title="Une climatisation adaptée à votre logement"
        image={IMAGES.clim}
        to="/climatisation"
        cta="Voir nos solutions de climatisation"
      >
        <p>Une installation performante commence par un dimensionnement adapté au logement : volumes, exposition, isolation, configuration des pièces et usage.</p>
        <p>HP Froid vous accompagne dans le choix de la solution la plus cohérente : mono-split, multi-split ou climatisation gainable.</p>
      </SplitBlock>
      <SplitBlock
        label="Froid commercial"
        title="Le froid pour les professionnels"
        image={IMAGES.froid}
        to="/froid-commercial"
        cta="Parler de votre installation"
        reverse
      >
        <p>HP Froid accompagne les professionnels pour l’installation, le remplacement, l’entretien et le dépannage de leurs équipements frigorifiques.</p>
        <p>Chambres froides positives et négatives, groupes frigorifiques et équipements de froid commercial.</p>
      </SplitBlock>
      <RealisationsPreview />
      <Manifesto />
      <Process />
      <ZoneAlsace />
      <Marques />
      <DepannageBand />
      <CTASection />
    </>
  );
}
